import { useState } from 'react';
import type { TFunction } from 'i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { CopyButton, RegistrationPlate, EntityViewers } from '@/components/common';
import { useNotify } from '@/hooks/useNotify';
import { WorkOrderStatusStepper } from './WorkOrderStatusStepper';
import { BookingReviewNotice } from './BookingReviewNotice';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';
import type { EntityViewer } from '@/hooks/useEntitySync';
import type { StatusHistoryEntry } from '@/types';

interface WorkOrderHeaderProps {
  workOrder: WorkOrderDetail;
  workOrderNumber: string;
  history: StatusHistoryEntry[];
  viewers: EntityViewer[];
  t: TFunction;
  tc: TFunction;
  onClose: () => void;
  onStatusChange: (targetStatus: string) => void;
  onUndo: () => void;
  statusChangeDisabled: boolean;
  undoDisabled: boolean;
  /** The plate opens the vehicle card (#3400). */
  onOpenVehicleCard?: () => void;
  statusActions?: React.ReactNode;
  /** Schedule bar rendered between the title row and the status stepper (#2105). */
  scheduleSlot?: React.ReactNode;
}

export function WorkOrderHeader({
  workOrder,
  workOrderNumber,
  history,
  viewers,
  t,
  tc,
  onClose,
  onStatusChange,
  onUndo,
  statusChangeDisabled,
  undoDisabled,
  onOpenVehicleCard,
  statusActions,
  scheduleSlot,
}: WorkOrderHeaderProps) {
  const notify = useNotify();
  const [linkCopied, setLinkCopied] = useState(false);

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/workorders/${workOrder.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      notify.success(t('linkCopied'));
      window.setTimeout(() => {
        setLinkCopied(false);
      }, 2000);
    } catch {
      notify.error(tc('error'), t('copyLinkFailed'));
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Tooltip title={tc('back')}>
          <IconButton onClick={onClose} aria-label={tc('back')}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <RegistrationPlate
              registrationNumber={workOrder.vehicle.registrationNumber}
              sx={{ fontSize: '1.25rem' }}
              copyable
              copyLabel={tc('copyField', { field: workOrder.vehicle.registrationNumber.toUpperCase() })}
              {...(onOpenVehicleCard ? { onClick: onOpenVehicleCard, openLabel: t('vehicleTab.openVehicleCard') } : {})}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {workOrderNumber}
              </Typography>
              <CopyButton
                value={workOrderNumber}
                label={tc('copyField', { field: workOrderNumber })}
              />
            </Box>
            <Tooltip title={linkCopied ? t('linkCopied') : t('copyLink')}>
              <IconButton
                size="small"
                onClick={() => void handleCopyLink()}
                aria-label={t('copyLink')}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <EntityViewers viewers={viewers} />
      </Box>

      {scheduleSlot}
      <BookingReviewNotice required={workOrder.requiresStaffReview} />

      <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', md: 'center' }, gap: 2, mb: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {workOrder.status === 'cancelled' ? (
            // The stepper has no step for a cancelled order, so the state was
            // invisible (#3402). Red, with the reason when one was given (#3358).
            <Alert severity="error" icon={<CancelIcon fontSize="inherit" />} data-testid="cancelled-status">
              <AlertTitle sx={{ mb: workOrder.cancellationReason ? 0.5 : 0 }}>{t('status.cancelled')}</AlertTitle>
              {workOrder.cancellationReason ? (
                <Typography variant="body2">
                  {t('cancelledReason')}: {workOrder.cancellationReason}
                </Typography>
              ) : null}
            </Alert>
          ) : (
            <WorkOrderStatusStepper
              currentStatus={workOrder.status}
              history={history}
              onStatusChange={onStatusChange}
              disabled={statusChangeDisabled}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {history.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              onClick={onUndo}
              disabled={undoDisabled}
            >
              {t('undoStatusChange')}
            </Button>
          )}
          {statusActions}
        </Box>
      </Box>
    </>
  );
}
